import {
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_HANDOFF_EASING,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_OPEN_HANDOFF_DURATION_MS,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_OPEN_REVEAL_CLIP_PATHS,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_OPEN_REVEAL_VISIBLE_CLIP_PATH,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_REFERENCE_FRAME_COUNT,
} from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';
import type {
  SpartanProductExteriorThreesixtyBackground,
  SpartanProductExteriorThreesixtyMediaRect,
  SpartanProductExteriorThreesixtyOpenRevealVariant,
} from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/types';

/** Smooth studio backdrop for the preview stage (avoids hard 50/50 panel splits). */
export const getThreesixtyStageBackground = (
  background: SpartanProductExteriorThreesixtyBackground | undefined
): string | undefined => {
  if (!background) return undefined;
  if (typeof background === 'string') return background;

  const { topEdgeColor, bottomEdgeColor } = background;
  if (!topEdgeColor) return background.panel;

  return `linear-gradient(180deg, ${topEdgeColor} 0%, ${bottomEdgeColor} 100%)`;
};

interface SpartanProductExteriorThreesixtyOpenRevealAnimation {
  keyframes: Keyframe[];
  options: KeyframeAnimationOptions;
}

const THREESIXTY_MEDIA_TRANSFORM_POSITION_EPSILON_PX = 0.5;
const THREESIXTY_MEDIA_TRANSFORM_SCALE_EPSILON = 0.001;

export const scaleThreesixtyFrameCount = (
  value: number,
  frameCount: number
) => {
  if (frameCount <= 0) return value;

  return Math.max(
    1,
    Math.round(
      value *
        (frameCount / SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_REFERENCE_FRAME_COUNT)
    )
  );
};

export const scaleThreesixtyPixelsPerFrame = (
  value: number,
  frameCount: number
) => {
  if (frameCount <= 0) return value;

  return (
    value *
    (SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_REFERENCE_FRAME_COUNT / frameCount)
  );
};

export const getThreesixtyOpenRevealClipPath = (
  variant: SpartanProductExteriorThreesixtyOpenRevealVariant
) => SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_OPEN_REVEAL_CLIP_PATHS[variant];

export const getThreesixtyOpenRevealAnimation = (
  shouldReduceMotion: boolean,
  variant: SpartanProductExteriorThreesixtyOpenRevealVariant = 'default'
): SpartanProductExteriorThreesixtyOpenRevealAnimation | null => {
  if (shouldReduceMotion) return null;

  return {
    keyframes: [
      {
        clipPath: getThreesixtyOpenRevealClipPath(variant),
      },
      {
        clipPath:
          SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_OPEN_REVEAL_VISIBLE_CLIP_PATH,
      },
    ],
    options: {
      duration: SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_OPEN_HANDOFF_DURATION_MS,
      easing: SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_HANDOFF_EASING,
      fill: 'both',
    },
  };
};

export const getThreesixtyMediaRect = (
  element: HTMLElement | null
): SpartanProductExteriorThreesixtyMediaRect | null => {
  if (!element) return null;

  const { top, left, width, height } = element.getBoundingClientRect();
  return { top, left, width, height };
};

export const getThreesixtyMediaTransform = (
  from: SpartanProductExteriorThreesixtyMediaRect,
  to: SpartanProductExteriorThreesixtyMediaRect
) => {
  if (from.width <= 0 || to.width <= 0) return null;

  const x = to.left - from.left;
  const y = to.top - from.top;
  const scale = to.width / from.width;

  if (
    Math.abs(x) < THREESIXTY_MEDIA_TRANSFORM_POSITION_EPSILON_PX &&
    Math.abs(y) < THREESIXTY_MEDIA_TRANSFORM_POSITION_EPSILON_PX &&
    Math.abs(scale - 1) < THREESIXTY_MEDIA_TRANSFORM_SCALE_EPSILON
  ) {
    return null;
  }

  return `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
};

export const getWrapDistance = (from: number, to: number, length: number) => {
  const delta = Math.abs(from - to);
  return Math.min(delta, length - delta);
};

export const wrapThreesixtyIndex = (index: number, length: number) => {
  if (length <= 0) return 0;

  const next = index % length;
  return next < 0 ? next + length : next;
};

export const getThreesixtySampledFrameIndexes = (
  frameCount: number,
  maximumFrameCount: number
) => {
  if (frameCount <= 0 || maximumFrameCount <= 0) return [];

  const sampledFrameCount = Math.min(frameCount, maximumFrameCount);
  return Array.from({ length: sampledFrameCount }, (_, index) =>
    Math.floor((index * frameCount) / sampledFrameCount)
  );
};

export const getThreesixtySampledPresetIndexes = (
  presetIndexes: readonly number[],
  sampledSourceIndexes: readonly number[],
  sourceFrameCount: number
) =>
  presetIndexes.map((presetIndex) => {
    const sourceIndex = wrapThreesixtyIndex(presetIndex, sourceFrameCount);
    let nearestSampleIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    sampledSourceIndexes.forEach((sampledSourceIndex, sampleIndex) => {
      const distance = getWrapDistance(
        sourceIndex,
        sampledSourceIndex,
        sourceFrameCount
      );
      if (distance >= nearestDistance) return;

      nearestDistance = distance;
      nearestSampleIndex = sampleIndex;
    });

    return nearestSampleIndex;
  });

export const getShortestThreesixtyFrameDelta = (
  from: number,
  to: number,
  frameCount: number
) => {
  if (frameCount <= 0) return 0;

  const forward = wrapThreesixtyIndex(to - from, frameCount);
  const backward = forward - frameCount;

  return Math.abs(forward) <= Math.abs(backward) ? forward : backward;
};
